// StudentSessionSearch.js
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QuizIcon from '@mui/icons-material/Quiz';
import { Formik } from 'formik';
import * as Yup from 'yup';
import API from '../api/api';
import { useDispatch } from 'react-redux';
import { setSnackError } from '../Reducers/snackErrors';
import { AWS_LINK, DEV_LINK } from '../Static/static';
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';


// Yup validation schema
const validationSchema = Yup.object({
  first_name: Yup.string()
    .trim()
    .min(2, 'First name is too short')
    .max(50, 'First name is too long')
    .matches(/^[a-zA-Z .'-]+$/, 'Use letters only')
    .required('First name is required'),
  last_name: Yup.string()
    .trim()
    .min(2, 'Last name is too short')
    .max(50, 'Last name is too long')
    .matches(/^[a-zA-Z .'-]+$/, 'Use letters only')
    .required('Last name is required'),
  join_code: Yup.string()
    .trim()
    .transform((value) => (value ? value.toUpperCase() : value))
    .matches(/^[A-Z0-9]{6,10}$/, 'Use 6–10 letters or numbers')
    .required('Join code is required'),
});

/**
 * Props:
 *   onSearch?: async ({ first_name, last_name, join_code }) => resultObject
 *
 * If onSearch is not provided, it will POST to /api/student-sessions/search by default.
 */
function StudentSessionSearch({ onSearch }) {
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const link = process.env.NODE_ENV === "production" ? AWS_LINK : DEV_LINK;
  


  const handleSubmit = async (values, formikHelpers) => {
    setLoading(true);
    setErrorMessage('');
    setResult(null);
    setSubmitting(true);
    try  {
      const payload = {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        join_code: values.join_code.trim().toUpperCase(),
      };
      
      const response = await axios.get(link+"/get_assessment_session", {params: payload} )
      if (response.data.data) {
        setResult(response.data.data);
      }
    }
    catch(error) {
      if (error.response.data) {
        setErrorMessage(error.response.data);
        return;
      }
      setErrorMessage('Unable to make request.')
    }
    finally {
      setLoading(false);
      setSubmitting(false);
      formikHelpers.setSubmitting(false);
    }

  };

  const navigate = useNavigate();

  const handleStartAssessment = () => {
    if (!result) { return ;}
    const url = `/assessment/${result.session_token}/${result.student_id}/${result.assessment_id}`;
    navigate(url);  
    
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 520,
        mx: 'auto',
        my: 4,
        px: 2,
      }}
    >
      <Paper
        elevation={4}
        sx={{
          p: 3,
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #121826, #1f2937)'
              : 'linear-gradient(135deg, #f4f4ff, #ffffff)',
        }}
      >
        <Stack spacing={2}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <QuizIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Find Your Test Session
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your name and join code to continue.
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 1 }} />

          <Formik
            initialValues={{
              first_name: '',
              last_name: '',
              join_code: '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnBlur
            validateOnChange={false}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit: formikSubmit,
            }) => (
              <Box component="form" noValidate onSubmit={formikSubmit}>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      label="First name"
                      name="first_name"
                      value={values.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.first_name && Boolean(errors.first_name)}
                      helperText={touched.first_name && errors.first_name}
                      size="small"
                      autoComplete="given-name"
                    />
                    <TextField
                      fullWidth
                      label="Last name"
                      name="last_name"
                      value={values.last_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.last_name && Boolean(errors.last_name)}
                      helperText={touched.last_name && errors.last_name}
                      size="small"
                      autoComplete="family-name"
                    />
                  </Stack>

                  <TextField
                    fullWidth
                    label="Join code"
                    name="join_code"
                    value={values.join_code}
                    onChange={(e) => {
                      // keep it uppercase as they type
                      e.target.value = e.target.value.toUpperCase();
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    error={touched.join_code && Boolean(errors.join_code)}
                    helperText={touched.join_code && errors.join_code}
                    size="small"
                    inputProps={{
                      maxLength: 10,
                      style: { letterSpacing: '0.12em', textTransform: 'uppercase' },
                    }}
                  />

                  {errorMessage && (
                    <Alert severity="error" variant="filled">
                      {errorMessage}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={!submitting && <SearchIcon />}
                      disabled={submitting}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        textTransform: 'none',
                        fontWeight: 'bold',
                      }}
                    >
                      {submitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        'Find my session'
                      )}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            )}
          </Formik>

          {/* Result section */}
          {result && (
            <Box mt={2}>
              <Alert severity="success" variant="outlined" sx={{ mb: 1 }}>
                Session found!
              </Alert>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Session details
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {result.title || 'Assessment'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Date:{new Date().toLocaleString()}
                </Typography>
                <br />
                <Button variant='contained' color='success' onClick={() => handleStartAssessment()}>
                  Start assessment
                </Button>
              </Paper>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}

export default StudentSessionSearch;